import { datadogRum } from '@datadog/browser-rum'
import { USER_JOURNEY_EVENTS } from './constants'
import { JourneyStartedEvent, RecordCompletedJourneyPayload } from './contract'

/**
 * Adapter between the journey reporting layer and wherever journeys are recorded. The
 * Datadog implementation is the Cycle 3 sink; a Path A implementation POSTing the
 * identical RecordCompletedJourney contract to the UJ service is a drop-in swap.
 */
export interface UserJourneySink {
  journeyStarted: (event: JourneyStartedEvent) => void
  journeyCompleted: (payload: RecordCompletedJourneyPayload) => void
}

type UserJourneyEventName = (typeof USER_JOURNEY_EVENTS)[keyof typeof USER_JOURNEY_EVENTS]

type UserJourneyTestHook = (entry: { type: UserJourneyEventName; event: JourneyStartedEvent | RecordCompletedJourneyPayload }) => void | Promise<void>

/**
 * e2e observability hook (CASHDAP-3410): Playwright specs register `window.__ujEventSink`
 * via exposeFunction before the app loads, and every sink emission is mirrored to it.
 * A function (not a `window.__ujEvents` array) because the checkout flow navigates back
 * to the merchant page, which would wipe any in-page buffer before the spec can read it —
 * exposeFunction survives navigations and collects on the test side. RUM itself is not
 * initialised in local e2e runs (no Datadog keys), so there is no intake to intercept.
 * Outside tests the property never exists and this is a no-op.
 */
const mirrorToTestHook = (type: UserJourneyEventName, event: JourneyStartedEvent | RecordCompletedJourneyPayload): void => {
  const hook = (window as Window & { __ujEventSink?: UserJourneyTestHook }).__ujEventSink
  if (typeof hook === 'function') {
    try {
      // An exposeFunction binding returns a Promise, and a Node-side throw arrives as an
      // async rejection the sync catch below cannot observe — swallow it explicitly too
      void Promise.resolve(hook({ type, event })).catch(() => {})
    } catch {
      // the test hook must never break real reporting
    }
  }
}

/**
 * `tags` mirrors the Cash proto as a flat `key:value` array, but Datadog facets on array
 * attributes are per-element — grouping by @context.tags yields one row per tag, so a
 * merchant or country split drowns in co-occurring tags, and facets can't match array
 * prefixes. Exploding each tag into a structured `tag_<key>` context field gives
 * dashboards a scalar facet to group on. Datadog-adapter concern only: the contract
 * payload stays proto-identical, and tagOnce guarantees one tag per key so fields never
 * collide.
 */
const explodeTags = (tags: string[]): Record<string, string> => {
  const fields: Record<string, string> = {}
  for (const tag of tags) {
    const separator = tag.indexOf(':')
    if (separator > 0) {
      fields[`tag_${tag.slice(0, separator)}`] = tag.slice(separator + 1)
    }
  }
  return fields
}

export const datadogUserJourneySink: UserJourneySink = {
  journeyStarted: (event) => {
    datadogRum.addAction(USER_JOURNEY_EVENTS.STARTED, event)
    mirrorToTestHook(USER_JOURNEY_EVENTS.STARTED, event)
  },
  journeyCompleted: (payload) => {
    datadogRum.addAction(USER_JOURNEY_EVENTS.COMPLETED, { ...payload, ...explodeTags(payload.tags) })
    // The hook observes the proto-shaped contract payload; exploded tag_* fields are a
    // Datadog-query concern only
    mirrorToTestHook(USER_JOURNEY_EVENTS.COMPLETED, payload)
  },
}
